import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { getStoreSession, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { loadingDotsAnimation } from "@store/theme/common/mixins/typography";
import { TYPOGRAPHY } from "@store/theme/design";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import { InfosBoxSize, InfosBoxType } from "@store/Widgets/InfosBox";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const InfosBox = getWidgetComponent("InfosBox");

type OwnProps = HasProduct;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getStoreSession(state);
    const productState = getProductState(state, ownProps.product.detail?.replacementProductId ?? undefined);

    return {
        supercededProduct: productState.value,
        supercededProductIsLoading: productState.isLoading,
        poolcorpSettings: getStoreSettingsCollection(state).poolcorpSettings,
        fulfillmentMethod: session.fulfillmentMethod,
        currentWarehouseDescription:
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.description
                : session.warehouse.description),
        currentWarehousePhone:
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.phone
                : session.warehouse.phone),
    };
};

const mapDispatchToProps = {
    loadProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StoreObsoleteSupercededProductLabelStyles {
    loadingText?: TypographyPresentationProps;
    supercededProductLink?: LinkProps;
}

const styles: StoreObsoleteSupercededProductLabelStyles = {
    loadingText: {
        css: css`
            ${loadingDotsAnimation()}
        `,
    },
    supercededProductLink: {
        typographyProps: {
            weight: TYPOGRAPHY.weight.bold,
        },
    },
};

export const storeObsoleteSupercededProductLabelStyles = styles;

const StoreObsoleteSupercededProductLabel: React.FC<Props> = ({ product, supercededProduct, loadProduct, poolcorpSettings, currentWarehouseDescription, currentWarehousePhone }) => {
    const isObsolete = getBooleanProperty(product.properties.obsolete);
    const isSuperceded = getBooleanProperty(product.properties.superceded);
    const itemObsoleteReason = product.properties.itemObsoleteReason;

    useEffect(() => {
        if (isSuperceded && product.detail?.replacementProductId) {
            loadProduct({
                id: product.detail.replacementProductId,
                onComplete: (result) => {
                    if (!result.apiResult) {
                        // Poolcorp Custom Code: this is when a superseded product is not found
                    }
                },
            });
        }
    }, []);

    const IsSupersedeDisabled =  !!poolcorpSettings?.productLinksToDisable.split(',').find((str) => str.trim().toLowerCase() === "supersede");

    if(IsSupersedeDisabled){
        return null;
    }

    if (!isObsolete && !isSuperceded) {
        return null;
    }

    const infoContent = () => {
        return (
            <Typography as="ul">
                {isObsolete && !itemObsoleteReason && (
                    <Typography as="li">{translate("This product is obsolete.")}</Typography>
                )}
                {isObsolete && itemObsoleteReason && (
                    <Typography as="li">{itemObsoleteReason} {currentWarehouseDescription} {currentWarehousePhone}</Typography>
                )}
                {isSuperceded && supercededProduct !== undefined ? (
                    <Typography as="li">
                        {translate("This product has been superceded by ")}
                        {supercededProduct ? (
                            <Link href={supercededProduct.canonicalUrl} {...styles.supercededProductLink}>
                                {supercededProduct.productNumber}
                            </Link>
                        ) : (
                            <Typography {...styles.loadingText} />
                        )}
                    </Typography>
                ) : (
                    <Typography as="li">
                        {translate("The superceded product was not found.")}
                    </Typography>)}
            </Typography>
        );
    };

    return <InfosBox type={InfosBoxType.Warning} size={InfosBoxSize.Small} content={infoContent()} />;
};

export default withProduct(connect(mapStateToProps, mapDispatchToProps)(StoreObsoleteSupercededProductLabel));
